// Story 213 — how the incident log reads VisionIncidentDto (03-dominio §C2, C7, C7.1).
import { format as formatDate } from 'date-fns'

// C11.9 (H-20): the name of each reason in its chip and in the filter. Design v2 (hub, 2026-10-22): «Móvil», with a key of
// its own, because "Phone" is the telephone of Users and Workers.
export const INCIDENT_REASON_TEXT = {
  PHONE: 'Mobile phone',
  NO_CAMERA: 'No camera',
  NOBODY: 'Nobody',
  CROWD: 'Too many people'
}

// Design v2 (hub, 2026-10-22): how an incident ended. The condition clearing says what a person saw, by reason; the other
// close causes keep their keys (C7).
const CLEARED_TEXT = {
  PHONE: 'Phone no longer seen',
  NO_CAMERA: 'Camera back',
  NOBODY: 'Someone came back',
  CROWD: 'Fewer people'
}
const CLOSE_CAUSE_TEXT = {
  CONDITION_CLEARED: 'Condition cleared',
  VIGILANCE_DISABLED: 'Vigilance disabled',
  SESSION_ENDED: 'Session ended',
  SERVER_RESTART: 'Server restart',
  LEFT_SENSITIVE_CONTENT: 'Left protected content',
  // Reopening 4 (C16.1): an admin stopped watching the session.
  WATCH_RELEASED: 'Stopped by an admin'
}

export const howItEnded = (incident, t_) => {
  if (incident.open === true) {
    return t_('Still open')
  }
  if (incident.closeCause === 'CONDITION_CLEARED' && CLEARED_TEXT[incident.reason]) {
    return t_(CLEARED_TEXT[incident.reason])
  }
  return CLOSE_CAUSE_TEXT[incident.closeCause] ? t_(CLOSE_CAUSE_TEXT[incident.closeCause]) : (incident.closeCause || '—')
}

// Hub, 2026-10-22: only these reasons raise an alarm for sure (NOBODY never does; NO_CAMERA may be silent, DN-2).
export const REASONS_WITH_ALARM = ['PHONE', 'CROWD']

// C2 v2, H-v2-7/14: Sun keeps the code of the sensitive type; Jupiter translates it.
export const SENSITIVE_TYPE_TEXT = {
  SCREEN: 'Screen',
  DOCUMENT: 'Document',
  WIDGET: 'Widget',
  PART_REFERENCE: 'Product',
  ORDER: 'Order',
  FABRICATION_ORDER: 'Fabrication order'
}

// C13.4, H9: what "What was being viewed" shows; MIC (scope ALL_MIC) is one more type there, with no filter chip of its own.
export const VIEWED_TYPE_TEXT = { ...SENSITIVE_TYPE_TEXT, MIC: 'MIC' }

// H-19: the family and major version of the browser in a userAgent (Edge says "Edg/" and also "Chrome/"; Chrome and
// Edge also say "Safari/"), or '' when none is recognised.
const BROWSERS = [
  ['Edge', /\bEdg\/(\d+)/],
  ['Firefox', /\bFirefox\/(\d+)/],
  ['Chrome', /\bChrome\/(\d+)/],
  ['Safari', /\bVersion\/(\d+)[^ ]* .*\bSafari\//]
]

export const browserText = (userAgent) => {
  const found = BROWSERS.map(([name, pattern]) => [name, pattern.exec(userAgent || '')]).find(([, match]) => match)
  return found ? `${found[0]} ${found[1][1]}` : ''
}

// C11.9: `clientIp · <browser>`, with `Unknown` (translated) for a browser it does not recognise.
export const equipmentText = (incident, t_) => {
  const browser = browserText(incident.userAgent) || t_('Unknown')
  return incident.clientIp ? `${incident.clientIp} · ${browser}` : browser
}

// S140, design v2 (Registro.dc.html, PO 2026-10-22): a duration in plain words — seconds alone under a minute, minutes and
// seconds under an hour, then total hours and minutes (past 24 h the hours go on counting, as the PO decided at GATE 2);
// empty when there is none (an open incident gets its time from its start).
export const humanDuration = (seconds) => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
    return ''
  }
  const whole = Math.floor(seconds)
  const hours = Math.floor(whole / 3600)
  const minutes = Math.floor((whole % 3600) / 60)
  const rest = whole % 60
  if (hours > 0) {
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`
  }
  if (minutes > 0) {
    return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`
  }
  return `${rest} s`
}

const sameLocalDay = (first, second) => first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() && first.getDate() === second.getDate()

// S119, design v2: when, in local time (DP-14): "Today 13:30" and "Yesterday 16:20", or the date in the format MIC uses;
// with seconds where the time must be exact (the detail).
export const whenText = (iso, { now, dateFormat, t_, withSeconds = false }) => {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const time = formatDate(date, withSeconds ? 'HH:mm:ss' : 'HH:mm')
  const today = new Date(now)
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  if (sameLocalDay(date, today)) {
    return `${t_('Today')} ${time}`
  }
  if (sameLocalDay(date, yesterday)) {
    return `${t_('Yesterday')} ${time}`
  }
  return `${formatDate(date, dateFormat)} ${time}`
}

// S120, design v2: the quick ranges of "When", by the days they cover counting today; "Dates…" uses the two dates.
export const RANGES = [
  { value: 'TODAY', text: 'Today', days: 1 },
  { value: '7_DAYS', text: '7 days', days: 7 },
  { value: '30_DAYS', text: '30 days', days: 30 },
  { value: 'DATES', text: 'Dates…', days: 0 }
]
export const DEFAULT_RANGE = '7_DAYS'

const toUtc = (date) => date.toISOString().replace(/\.\d{3}Z$/, 'Z')

// The local midnight of the first day of a quick range, in UTC (DP-14); '' for "Dates…".
export const rangeStartUtc = (range, now) => {
  const found = RANGES.find((option) => option.value === range)
  if (!found || found.days === 0) {
    return ''
  }
  const today = new Date(now)
  return toUtc(new Date(today.getFullYear(), today.getMonth(), today.getDate() - (found.days - 1)))
}

// DP-14: the API is in UTC (ISO-8601 without milliseconds); a `datetime-local` value is local time.
export const localInputToUtc = (localValue) => {
  if (!localValue) {
    return ''
  }
  const date = new Date(localValue)
  return Number.isNaN(date.getTime()) ? '' : toUtc(date)
}

/**
 * The query of GET /vision/incidents (C1, C11.9, design v2): only the filters that are set, and always the page (from
 * 0). A quick range sends its start; "Dates…" the two dates typed. "Only open" sends `open` and no dates, so it lists
 * every open incident, the ones its counter counts. H-21: the search `q` replaces the user filter; no `userId` is sent.
 */
export const incidentsQuery = ({ range, from, to, reason, sensitiveType, q, onlyOpen }, page, now) => {
  const query = {}
  const search = (q || '').trim()
  if (onlyOpen) {
    query.open = true
  } else if (range === 'DATES') {
    const fromUtc = localInputToUtc(from)
    const toUtcValue = localInputToUtc(to)
    if (fromUtc) query.from = fromUtc
    if (toUtcValue) query.to = toUtcValue
  } else {
    const start = rangeStartUtc(range, now)
    if (start) query.from = start
  }
  if (reason) query.reason = reason
  if (sensitiveType) query.sensitiveType = sensitiveType
  if (search) query.q = search
  query.page = page
  return query
}

// Design v2 (Registro.dc.html): Camera control opens the log with this state, and the log then offers the way back.
export const FROM_CAMERA_CONTROL = { from: 'cameraControl' }

// C11.9: the counter "N open now" is the total of this query.
export const OPEN_NOW_QUERY = { open: true, size: 1 }
